import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import ApprovalRequestService from "../services/approvalRequest.service";
import { IApprovalRequestsState } from "../types";
import { StoreState } from "@/store/type";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import analyticsService from "@/analytic.service";
import { APPROVAL_REQUEST_ANALYTICS_EVENTS } from "../constants/analyticsEvents";
import { FetchApprovalRequestsParams } from "../models/fetchApprovalRequestsParams.request";
import {
  ApprovalRequestActionPayload,
  ApprovalRequestCancelPayload,
  ApprovalRequestOverridePayload,
} from "../models/approvalRequestAction.request";
import {
  unwrapApprovalRequestDetail,
  unwrapApprovalRequestList,
} from "../utils/normalizeApprovalRequest";

const approvalRequestService = new ApprovalRequestService();

const actions: ActionTree<IApprovalRequestsState, StoreState> = {
  async fetchApprovalRequests(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    params: FetchApprovalRequestsParams = {},
  ) {
    try {
      context.commit("setFetchingApprovalRequests", true);
      const response: ICoreResponse =
        await approvalRequestService.listApprovalRequests(params);

      if (response.success && response.status === StatusCodes.OK) {
        const { items, total } = unwrapApprovalRequestList(response.data);
        context.commit("setApprovalRequests", items);
        context.commit("setTotalRecords", total);
        if (params.page) context.commit("setPage", params.page);
        if (params.page_size) context.commit("setPageSize", params.page_size);
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_LIST_FETCH,
          params,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setApprovalRequests", []);
      context.commit("setTotalRecords", 0);
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingApprovalRequests", false);
    }
  },

  async fetchApprovalRequestDetail(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    payload: {
      requestId: string;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      context.commit("setFetchingApprovalRequestDetail", true);
      const response: ICoreResponse =
        await approvalRequestService.getApprovalRequestDetail(
          payload.requestId,
        );

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        context.commit("setSelectedApprovalRequest", detail);
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_DETAIL_FETCH,
          { requestId: payload.requestId },
        );
        payload.successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setSelectedApprovalRequest", null);
      payload.errorHandler?.();
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("setFetchingApprovalRequestDetail", false);
    }
  },

  async actionApprovalRequest(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      requestId,
      body,
      successHandler,
      errorHandler,
    }: {
      requestId: string;
      body: ApprovalRequestActionPayload;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setApprovalRequestActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.actionApprovalRequest(requestId, body);

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        if (detail) {
          context.commit("setSelectedApprovalRequest", detail);
        }
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_ACTION,
          { requestId, actions: body.actions },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_REQUEST_ACTION_SUCCESS;
        successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalRequestActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async overrideApprovalRequest(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      requestId,
      body,
      successHandler,
      errorHandler,
    }: {
      requestId: string;
      body: ApprovalRequestOverridePayload;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setApprovalRequestActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.overrideApprovalRequest(requestId, body);

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        if (detail) {
          context.commit("setSelectedApprovalRequest", detail);
        }
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_OVERRIDE,
          { requestId, status: body.status },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_REQUEST_OVERRIDE_SUCCESS;
        successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalRequestActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async cancelApprovalRequest(
    context: ActionContext<IApprovalRequestsState, StoreState>,
    {
      requestId,
      body,
      successHandler,
      errorHandler,
    }: {
      requestId: string;
      body: ApprovalRequestCancelPayload;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setApprovalRequestActionLoading", true);
      const response: ICoreResponse =
        await approvalRequestService.cancelApprovalRequest(requestId, body);

      if (response.success && response.status === StatusCodes.OK) {
        const detail = unwrapApprovalRequestDetail(response.data);
        if (detail) {
          context.commit("setSelectedApprovalRequest", detail);
        }
        analyticsService.logActionEvent(
          APPROVAL_REQUEST_ANALYTICS_EVENTS.APPROVAL_REQUEST_CANCEL,
          { requestId },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.APPROVAL_REQUEST_CANCEL_SUCCESS;
        successHandler?.();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setApprovalRequestActionLoading", false);
      notificationService.triggerNotification();
    }
  },
};

export default actions;
